import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Bar,
  Brush,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MaskValor } from '../../../../../component';
import { IChartProps, StickyTooltip } from '../../../../../component/charts';
import ChartCard from '../../../../../component/charts/chartCard';
import {
  labelFormatterMoneyAsInteger,
  labelFormatterNumber,
} from '../../../../../shared/formatters/formatters';
import { ReleasesSalesEcoMapChartData } from './types';

const LaunchedUnitsECOMAP = React.memo(function LaunchedUnits() {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const MemoLaunchedUnitsECOMAPChart = React.memo(LaunchedUnitsECOMAPChart);
  return requestStatus['launchedUnits'] === 'LOADED' ? (
    <ChartCard
      data={chartsDataPrimary['eco_map']}
      Chart={MemoLaunchedUnitsECOMAPChart}
      cy={'LaunchedUnitsECOMAP'}
      id={'launchedUnitsECOMAP'}
      requestStatus={requestStatus['launchedUnits']}
    />
  ) : (
    <></>
  );
});

const LaunchedUnitsECOMAPChart: React.FC<
  IChartProps<ReleasesSalesEcoMapChartData[]>
> = ({ data, cy, id }) => {
  const type = '_' + data[0]['type' as keyof (typeof data)[0]];
  const [hasClicked, setHasClicked] = useState<boolean>(false);
  const [recordTooltip, setRecordTooltip] = useState<JSX.Element | null>(null);
  const [stopChange, setStopChange] = useState<boolean>(false);
  const formatterFunctions = [labelFormatterNumber, labelFormatterNumber];
  return (
    <ResponsiveContainer
      data-cy={`LaunchedUnits${cy}`}
      width={'100%'}
      height={'100%'}
    >
      <ComposedChart
        data={data}
        id={id}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        onClick={() => setHasClicked(!hasClicked)}
      >
        <Tooltip
          wrapperStyle={{ visibility: 'visible' }}
          content={
            <StickyTooltip
              hasClicked={hasClicked}
              recordTooltip={recordTooltip}
              setRecordTooltip={setRecordTooltip}
              stopChange={stopChange}
              setStopChange={setStopChange}
              formatterFunctions={formatterFunctions}
            />
          }
        />
        <XAxis dataKey="period" />
        <XAxis />
        <Bar
          isAnimationActive={false}
          dataKey="total_releases"
          name="Lançamentos"
          fill="#8884d8"
        >
          <LabelList
            dataKey="total_releases"
            position="top"
            formatter={(v: any) => MaskValor(v)}
          />
        </Bar>
        <Brush dataKey="period" height={30} stroke="#8884d8" />
        <Line
          isAnimationActive={false}
          name="Média lançamentos"
          type="monotone"
          dataKey={`total_releases_average${type}`}
          stroke="#ff0000"
        />
        <Legend iconType="circle" />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export const LaunchedUnitsECO: React.FC = () => {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  return (
    <ChartCard
      data={chartsDataPrimary['eco_map']}
      Chart={LaunchedUnitsECOChart}
      cy={'LaunchedUnitsECO'}
      id={'launchedUnitsECO'}
      requestStatus={requestStatus['launchedUnits']}
    />
  );
};

const LaunchedUnitsECOChart: React.FC<
  IChartProps<ReleasesSalesEcoMapChartData[]>
> = ({ data, cy, id }) => {
  const type = '_' + data[0]['type' as keyof (typeof data)[0]];
  const [hasClicked, setHasClicked] = useState<boolean>(false);
  const [recordTooltip, setRecordTooltip] = useState<JSX.Element | null>(null);
  const [stopChange, setStopChange] = useState<boolean>(false);
  const formatterFunctions = [
    labelFormatterNumber,
    labelFormatterMoneyAsInteger,
  ];
  return (
    <ResponsiveContainer data-cy={`LaunchedUnits${cy}`}>
      <ComposedChart
        data={data}
        id={id}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        onClick={() => setHasClicked(!hasClicked)}
      >
        <Tooltip
          content={
            <StickyTooltip
              hasClicked={hasClicked}
              recordTooltip={recordTooltip}
              setRecordTooltip={setRecordTooltip}
              stopChange={stopChange}
              setStopChange={setStopChange}
              formatterFunctions={formatterFunctions}
            />
          }
          wrapperStyle={{ visibility: 'visible' }}
        />
        <XAxis dataKey="period" />
        <YAxis />
        <Bar
          isAnimationActive={false}
          dataKey="eco_releases_count"
          name="ECO"
          fill="#8884d8"
        >
          <LabelList
            dataKey="eco_releases_count"
            position="top"
            formatter={(v: any) => MaskValor(v)}
          />
        </Bar>
        <Line
          isAnimationActive={false}
          name="Média lançamentos em VGV"
          type="monotone"
          dataKey={`eco_releases_average${type}`}
          stroke="#ff0000"
        />
        <Legend iconType="circle" />
        <Brush dataKey="period" height={30} stroke="#8884d8" />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export const LaunchedUnitsMAP: React.FC = () => {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  return (
    <ChartCard
      data={chartsDataPrimary['eco_map']}
      Chart={LaunchedUnitsMAPChart}
      cy={'LaunchedUnitsMAP'}
      id={'launchedUnitsMAP'}
      requestStatus={requestStatus['launchedUnits']}
    />
  );
};

const LaunchedUnitsMAPChart: React.FC<
  IChartProps<ReleasesSalesEcoMapChartData[]>
> = ({ data, cy, id }) => {
  const type = '_' + data[0]['type' as keyof (typeof data)[0]];
  const [hasClicked, setHasClicked] = useState<boolean>(false);
  const [recordTooltip, setRecordTooltip] = useState<JSX.Element | null>(null);
  const [stopChange, setStopChange] = useState<boolean>(false);
  const formatterFunctions = [labelFormatterNumber, labelFormatterNumber];
  return (
    <ResponsiveContainer data-cy={`LaunchedUnits${cy}`}>
      <ComposedChart
        data={data}
        id={id}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        onClick={() => setHasClicked(!hasClicked)}
      >
        <Tooltip
          wrapperStyle={{ visibility: 'visible' }}
          content={
            <StickyTooltip
              hasClicked={hasClicked}
              recordTooltip={recordTooltip}
              setRecordTooltip={setRecordTooltip}
              stopChange={stopChange}
              setStopChange={setStopChange}
              formatterFunctions={formatterFunctions}
            />
          }
        />
        <XAxis dataKey="period" />
        <YAxis />
        <Bar
          isAnimationActive={false}
          dataKey="map_releases_count"
          name="MAP"
          fill="#8884d8"
        >
          <LabelList
            dataKey="map_releases_count"
            position="top"
            formatter={(v: any) => MaskValor(v)}
          />
        </Bar>
        <Line
          isAnimationActive={false}
          name="Média lançamentos em VGV"
          type="monotone"
          dataKey={`map_releases_average${type}`}
          stroke="#ff0000"
        />
        <Legend iconType="circle" />
        <Brush dataKey="period" height={30} stroke="#8884d8" />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export { LaunchedUnitsECOMAP };
